import React, { useState, useEffect, useRef } from 'react';

const WriscoreGraph = ({ scores, originalText }) => {
    const [hoveredError, setHoveredError] = useState(null);
    const [expandedSections, setExpandedSections] = useState({ grammar: true, punctuation: true, submission: true });
    const hasLoggedRef = useRef(false);

    useEffect(() => {
        if (hasLoggedRef.current || (!scores && !originalText)) return;
        console.log('🔍 WriscoreGraph:', { scores, grammarErrors: scores?.grammarErrors?.length, punctuationErrors: scores?.punctuationErrors?.length });
        hasLoggedRef.current = true;
    }, [scores, originalText]);

    // ─── Derived values ────────────────────────────────────────────────
    const totalScore   = Math.round(scores?.total              || scores?.overallScore          || 0);
    const coherence    = Math.round(scores?.coherence          || scores?.contentScore          || 0);
    const vocabulary   = Math.round(scores?.vocabulary         || scores?.vocabularyScore       || 0);
    const readability  = Math.round(scores?.grammar            || scores?.readabilityScore      || 0);
    const fulfillment  = Math.round(scores?.taskFulfillment    || scores?.taskFulfillmentScore  || 0);
    const grammarErrors       = scores?.grammarErrors       || [];
    const punctuationErrors   = scores?.punctuationErrors   || [];
    const wordCount           = scores?.wordCount           || scores?.wordCountIssues?.count   || 0;

    const getCLBLevel = (s) => {
        if (s >= 10) return { level: 'CLB 10–12', color: '#15803d', bg: '#dcfce7' };
        if (s >= 9)  return { level: 'CLB 9',     color: '#1d4ed8', bg: '#dbeafe' };
        if (s >= 8)  return { level: 'CLB 8',     color: '#7c3aed', bg: '#ede9fe' };
        if (s >= 7)  return { level: 'CLB 7',     color: '#b45309', bg: '#fef3c7' };
        if (s >= 6)  return { level: 'CLB 6',     color: '#d97706', bg: '#fff7ed' };
        if (s >= 5)  return { level: 'CLB 5',     color: '#dc2626', bg: '#fee2e2' };
        return            { level: 'CLB M–4',    color: '#991b1b', bg: '#fecaca' };
    };

    const getBarColor = (s) => {
        if (s >= 10) return { bar: '#15803d', bg: '#dcfce7' };
        if (s >= 8)  return { bar: '#1d4ed8', bg: '#dbeafe' };
        if (s >= 6)  return { bar: '#7c3aed', bg: '#ede9fe' };
        if (s >= 4)  return { bar: '#d97706', bg: '#fef3c7' };
        return            { bar: '#dc2626', bg: '#fee2e2' };
    };

    const clb = getCLBLevel(totalScore);
    const pct = Math.round((totalScore / 12) * 100);

    const dimensions = [
        { label: 'Task Fulfillment',   value: fulfillment,  icon: '🎯' },
        { label: 'Content/Coherence',  value: coherence,    icon: '🔗' },
        { label: 'Vocabulary',         value: vocabulary,    icon: '📚' },
        { label: 'Readability',        value: readability,  icon: '✍️' },
    ];

    const toggleSection = (key) =>
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

    // ─── Inline highlighted text ──────────────────────────────────────
    const renderHighlightedText = () => {
        if (!originalText) return null;
        const allErrors = [
            ...grammarErrors.map(e => ({ ...e, category: 'grammar' })),
            ...punctuationErrors.map(e => ({ ...e, category: 'punctuation' }))
        ].filter(e => e.position).sort((a, b) => (a.position.start || 0) - (b.position.start || 0));

        if (!allErrors.length) {
            return (
                <div style={{ padding: '18px 20px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', lineHeight: 1.8, fontSize: 15, color: '#374151', whiteSpace: 'pre-wrap' }}>
                    {originalText}
                </div>
            );
        }

        const segments = [];
        let lastIndex = 0;
        allErrors.forEach((error, idx) => {
            const { position, incorrect, correct, type, category } = error;
            if (position.start < 0 || position.end > originalText.length || position.start < lastIndex) return;
            if (position.start > lastIndex) {
                segments.push(<span key={`t-${idx}`}>{originalText.substring(lastIndex, position.start)}</span>);
            }
            const isGrammar = category === 'grammar';
            segments.push(
                <span key={`e-${idx}`} style={{ position: 'relative', display: 'inline' }}
                    onMouseEnter={() => setHoveredError(idx)}
                    onMouseLeave={() => setHoveredError(null)}
                >
                    <span style={{
                        background: isGrammar ? '#fee2e2' : '#fef3c7',
                        color: isGrammar ? '#dc2626' : '#b45309',
                        textDecoration: 'line-through',
                        padding: '1px 3px', borderRadius: 3, fontWeight: 500
                    }}>{incorrect}</span>
                    <span style={{
                        background: '#dcfce7', color: '#15803d',
                        padding: '1px 5px', borderRadius: 3, fontWeight: 700,
                        border: '1px solid #86efac', marginLeft: 3
                    }}>{correct}</span>
                    {hoveredError === idx && (
                        <span style={{
                            position: 'absolute', bottom: '110%', left: '50%', transform: 'translateX(-50%)',
                            background: '#1e293b', color: '#fff', padding: '5px 10px',
                            borderRadius: 5, fontSize: 12, whiteSpace: 'nowrap', zIndex: 999,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.25)'
                        }}>
                            {isGrammar ? '🔴' : '🟠'} {type || (isGrammar ? 'Grammar Error' : 'Punctuation Error')}
                        </span>
                    )}
                </span>
            );
            lastIndex = position.end;
        });
        if (lastIndex < originalText.length) segments.push(<span key="tend">{originalText.substring(lastIndex)}</span>);
        return (
            <div style={{ padding: '18px 20px', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', lineHeight: 2, fontSize: 15, color: '#374151', whiteSpace: 'pre-wrap' }}>
                {segments}
            </div>
        );
    };

    // ─── Error row ────────────────────────────────────────────────────
    const ErrorRow = ({ err, idx, isGrammar }) => {
        const incorrect = typeof err === 'string' ? err : err.incorrect;
        const correct   = typeof err === 'string' ? ''  : err.correct;
        const type      = typeof err === 'string' ? ''  : err.type;
        return (
            <div key={idx} style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '10px 14px', borderRadius: 8,
                background: isGrammar ? '#fff5f5' : '#fffbeb',
                border: `1px solid ${isGrammar ? '#fca5a5' : '#fde68a'}`,
                marginBottom: 8
            }}>
                <span style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}>{isGrammar ? '🔴' : '🟠'}</span>
                <div style={{ flex: 1, fontSize: 14, lineHeight: 1.5 }}>
                    <span style={{ textDecoration: 'line-through', color: isGrammar ? '#dc2626' : '#b45309', fontWeight: 600 }}>
                        {incorrect}
                    </span>
                    {correct && (
                        <>
                            <span style={{ color: '#9ca3af', margin: '0 6px' }}>→</span>
                            <span style={{ color: '#15803d', fontWeight: 700 }}>{correct}</span>
                        </>
                    )}
                    {type && (
                        <span style={{
                            marginLeft: 8, fontSize: 12, color: '#6b7280',
                            fontStyle: 'italic', background: '#f3f4f6',
                            padding: '1px 6px', borderRadius: 10
                        }}>
                            {type}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    // ─── Collapsible section header ───────────────────────────────────
    const SectionHeader = ({ title, count, icon, colorAccent, isOpen, onToggle }) => (
        <div onClick={onToggle} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 18px', cursor: 'pointer',
            background: '#f8fafc', borderRadius: isOpen ? '10px 10px 0 0' : 10,
            border: `1.5px solid ${colorAccent}`,
            userSelect: 'none'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18 }}>{icon}</span>
                <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>{title}</span>
                <span style={{
                    background: colorAccent, color: '#fff',
                    borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700
                }}>{count}</span>
            </div>
            <span style={{ color: colorAccent, fontWeight: 700, fontSize: 14 }}>{isOpen ? '▲ Hide' : '▼ Show'}</span>
        </div>
    );

    return (
        <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif', color: '#1e293b', padding: '4px 0' }}>

            {/* ── TOP SUMMARY STATS ─────────────────────────────────── */}
            <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 28
            }}>
                {/* Final Score */}
                <div style={{
                    flex: '1 1 140px', background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)',
                    borderRadius: 14, padding: '18px 20px', color: '#fff',
                    textAlign: 'center', boxShadow: '0 4px 16px rgba(59,130,246,0.35)'
                }}>
                    <div style={{ fontSize: 13, fontWeight: 600, opacity: 0.85, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Final Score</div>
                    <div style={{ fontSize: 40, fontWeight: 900, lineHeight: 1 }}>{totalScore}</div>
                    <div style={{ fontSize: 14, opacity: 0.75, marginTop: 2 }}>out of 12 &nbsp;·&nbsp; {pct}%</div>
                </div>

                {/* CLB Level */}
                <div style={{
                    flex: '1 1 120px', background: clb.bg,
                    border: `2px solid ${clb.color}`,
                    borderRadius: 14, padding: '18px 20px',
                    textAlign: 'center'
                }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: clb.color, opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>CLB Level</div>
                    <div style={{ fontSize: 26, fontWeight: 900, color: clb.color }}>{clb.level}</div>
                    <div style={{ fontSize: 12, color: clb.color, opacity: 0.7, marginTop: 3 }}>Benchmark</div>
                </div>

                {/* Grammar Errors */}
                <div style={{
                    flex: '1 1 110px', background: grammarErrors.length === 0 ? '#dcfce7' : '#fee2e2',
                    border: `2px solid ${grammarErrors.length === 0 ? '#86efac' : '#fca5a5'}`,
                    borderRadius: 14, padding: '18px 20px', textAlign: 'center'
                }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: grammarErrors.length === 0 ? '#15803d' : '#dc2626', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
                        Grammar
                    </div>
                    <div style={{ fontSize: 32, fontWeight: 900, color: grammarErrors.length === 0 ? '#15803d' : '#dc2626' }}>
                        {grammarErrors.length}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>
                        {grammarErrors.length === 0 ? 'No errors ✓' : `error${grammarErrors.length !== 1 ? 's' : ''}`}
                    </div>
                </div>

                {/* Punctuation Errors */}
                <div style={{
                    flex: '1 1 110px', background: punctuationErrors.length === 0 ? '#dcfce7' : '#fef3c7',
                    border: `2px solid ${punctuationErrors.length === 0 ? '#86efac' : '#fde68a'}`,
                    borderRadius: 14, padding: '18px 20px', textAlign: 'center'
                }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: punctuationErrors.length === 0 ? '#15803d' : '#b45309', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
                        Punctuation
                    </div>
                    <div style={{ fontSize: 32, fontWeight: 900, color: punctuationErrors.length === 0 ? '#15803d' : '#b45309' }}>
                        {punctuationErrors.length}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>
                        {punctuationErrors.length === 0 ? 'No errors ✓' : `error${punctuationErrors.length !== 1 ? 's' : ''}`}
                    </div>
                </div>

                {/* Word Count */}
                {wordCount > 0 && (
                    <div style={{
                        flex: '1 1 110px', background: '#f0f9ff',
                        border: '2px solid #93c5fd',
                        borderRadius: 14, padding: '18px 20px', textAlign: 'center'
                    }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Words</div>
                        <div style={{ fontSize: 32, fontWeight: 900, color: '#1d4ed8' }}>{wordCount}</div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>Total words</div>
                    </div>
                )}
            </div>

            {/* ── SCORE DIMENSION BARS ─────────────────────────────── */}
            <div style={{
                background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 14,
                padding: '20px 24px', marginBottom: 24,
                boxShadow: '0 1px 6px rgba(0,0,0,0.06)'
            }}>
                <h3 style={{ margin: '0 0 18px', fontSize: 15, fontWeight: 800, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>📊</span> Score Breakdown
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {dimensions.map(({ label, value, icon }) => {
                        const colors = getBarColor(value);
                        const barPct = Math.round((value / 12) * 100);
                        return (
                            <div key={label}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                                    <span style={{ fontSize: 13.5, fontWeight: 600, color: '#374151' }}>
                                        {icon} {label}
                                    </span>
                                    <span style={{
                                        fontSize: 13, fontWeight: 800,
                                        background: colors.bg, color: colors.bar,
                                        padding: '2px 10px', borderRadius: 20, border: `1px solid ${colors.bar}20`
                                    }}>
                                        {value}/12
                                    </span>
                                </div>
                                <div style={{ background: '#f1f5f9', borderRadius: 8, height: 12, overflow: 'hidden' }}>
                                    <div style={{
                                        width: `${barPct}%`, height: '100%',
                                        background: `linear-gradient(90deg, ${colors.bar}cc, ${colors.bar})`,
                                        borderRadius: 8,
                                        transition: 'width 1s ease',
                                        minWidth: value > 0 ? 20 : 0
                                    }} />
                                </div>
                            </div>
                        );
                    })}

                    {/* Overall score highlight bar */}
                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                            <span style={{ fontSize: 14, fontWeight: 800, color: '#1e293b' }}>🏆 Overall Score</span>
                            <span style={{
                                fontSize: 14, fontWeight: 900, color: '#fff',
                                background: '#1d4ed8', padding: '3px 14px', borderRadius: 20
                            }}>
                                {totalScore}/12 · {pct}%
                            </span>
                        </div>
                        <div style={{ background: '#e0e7ff', borderRadius: 8, height: 14, overflow: 'hidden' }}>
                            <div style={{
                                width: `${pct}%`, height: '100%',
                                background: 'linear-gradient(90deg, #1d4ed8, #3b82f6)',
                                borderRadius: 8, transition: 'width 1s ease',
                                minWidth: totalScore > 0 ? 20 : 0
                            }} />
                        </div>
                    </div>
                </div>
            </div>

            {/* ── GRAMMAR ERRORS ───────────────────────────────────── */}
            {(grammarErrors.length > 0) && (
                <div style={{ marginBottom: 16, borderRadius: 10, overflow: 'hidden', border: '1.5px solid #fca5a5' }}>
                    <SectionHeader
                        title="Grammar Errors"
                        count={grammarErrors.length}
                        icon="📝"
                        colorAccent="#dc2626"
                        isOpen={expandedSections.grammar}
                        onToggle={() => toggleSection('grammar')}
                    />
                    {expandedSections.grammar && (
                        <div style={{ padding: '14px 16px', background: '#fff' }}>
                            {grammarErrors.map((err, idx) => (
                                <ErrorRow key={idx} err={err} idx={idx} isGrammar={true} />
                            ))}
                        </div>
                    )}
                </div>
            )}
            {grammarErrors.length === 0 && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '12px 16px', background: '#dcfce7', border: '1.5px solid #86efac',
                    borderRadius: 10, marginBottom: 16, fontSize: 14, fontWeight: 600, color: '#15803d'
                }}>
                    <span style={{ fontSize: 18 }}>✅</span> No grammar errors found — excellent work!
                </div>
            )}

            {/* ── PUNCTUATION ERRORS ───────────────────────────────── */}
            {punctuationErrors.length > 0 && (
                <div style={{ marginBottom: 16, borderRadius: 10, overflow: 'hidden', border: '1.5px solid #fde68a' }}>
                    <SectionHeader
                        title="Punctuation Errors"
                        count={punctuationErrors.length}
                        icon="✏️"
                        colorAccent="#b45309"
                        isOpen={expandedSections.punctuation}
                        onToggle={() => toggleSection('punctuation')}
                    />
                    {expandedSections.punctuation && (
                        <div style={{ padding: '14px 16px', background: '#fff' }}>
                            {punctuationErrors.map((err, idx) => (
                                <ErrorRow key={idx} err={err} idx={idx} isGrammar={false} />
                            ))}
                        </div>
                    )}
                </div>
            )}
            {punctuationErrors.length === 0 && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '12px 16px', background: '#dcfce7', border: '1.5px solid #86efac',
                    borderRadius: 10, marginBottom: 16, fontSize: 14, fontWeight: 600, color: '#15803d'
                }}>
                    <span style={{ fontSize: 18 }}>✅</span> No punctuation errors found — great precision!
                </div>
            )}

            {/* ── WORD COUNT FEEDBACK ──────────────────────────────── */}
            {scores?.wordCountIssues && typeof scores.wordCountIssues === 'object' && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 16px', borderRadius: 10, marginBottom: 16,
                    background: scores.wordCountIssues.status === 'valid' ? '#f0f9ff' : '#fff7ed',
                    border: `1.5px solid ${scores.wordCountIssues.status === 'valid' ? '#93c5fd' : '#fdba74'}`,
                    fontSize: 14
                }}>
                    <span style={{ fontSize: 18 }}>📊</span>
                    <span style={{ fontWeight: 700, color: '#1e293b' }}>
                        Word Count: {scores.wordCountIssues.count} words
                    </span>
                    <span style={{ color: '#6b7280' }}>
                        (Required: {scores.wordCountIssues.min}–{scores.wordCountIssues.max})
                    </span>
                    {scores.wordCountIssues.status !== 'valid' && (
                        <span style={{ color: '#ea580c', fontWeight: 700 }}>
                            ⚠️ {scores.wordCountIssues.status === 'too_short' ? 'Below minimum' : 'Above maximum'}
                        </span>
                    )}
                </div>
            )}

            {/* ── YOUR SUBMISSION WITH CORRECTIONS ─────────────────── */}
            {originalText && (
                <div style={{ marginBottom: 20 }}>
                    <div onClick={() => toggleSection('submission')} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '14px 18px', cursor: 'pointer',
                        background: '#f8fafc', borderRadius: expandedSections.submission ? '10px 10px 0 0' : 10,
                        border: '1.5px solid #cbd5e1', userSelect: 'none'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                            <span>📄</span> Your Submission with Corrections
                        </div>
                        <span style={{ color: '#64748b', fontWeight: 700, fontSize: 14 }}>
                            {expandedSections.submission ? '▲ Hide' : '▼ Show'}
                        </span>
                    </div>
                    {expandedSections.submission && (
                        <div style={{ border: '1.5px solid #cbd5e1', borderTop: 'none', borderRadius: '0 0 10px 10px', padding: 16, background: '#fff' }}>
                            {/* Legend */}
                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12, fontSize: 12 }}>
                                <span><span style={{ background: '#fee2e2', color: '#dc2626', padding: '1px 6px', borderRadius: 4, textDecoration: 'line-through', fontWeight: 600 }}>incorrect</span> <span style={{ color: '#6b7280' }}>Grammar error</span></span>
                                <span><span style={{ background: '#fef3c7', color: '#b45309', padding: '1px 6px', borderRadius: 4, textDecoration: 'line-through', fontWeight: 600 }}>incorrect</span> <span style={{ color: '#6b7280' }}>Punctuation error</span></span>
                                <span><span style={{ background: '#dcfce7', color: '#15803d', padding: '1px 6px', borderRadius: 4, fontWeight: 700, border: '1px solid #86efac' }}>correction</span></span>
                            </div>
                            {renderHighlightedText()}
                        </div>
                    )}
                </div>
            )}

            {/* ── IMPROVEMENT TIP ──────────────────────────────────── */}
            {scores?.improvement && (
                <div style={{
                    padding: '16px 20px', background: '#f0fdf4',
                    border: '1.5px solid #86efac', borderRadius: 12,
                    display: 'flex', gap: 12, alignItems: 'flex-start'
                }}>
                    <span style={{ fontSize: 22, flexShrink: 0 }}>💡</span>
                    <div>
                        <div style={{ fontWeight: 800, color: '#15803d', fontSize: 14, marginBottom: 4 }}>Improvement Tip</div>
                        <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.65 }}>{scores.improvement}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WriscoreGraph;
